import { Container, Row, Col } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import { getTimeInOutPeriod, peekTimeInOut, payPeriodRange } from '../../../services/ApiGetTimeInOut';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TimeInOut } from '../../../model/datatype';

// How many earlier periods can be viewed besides the current one.
const MAX_OLDER_PERIODS = 2;

type PeriodState = TimeInOut | 'error' | undefined;

export const TimeInOutThisWeek = (props: any) => {
  let { t } = useTranslation();
  // How many pay periods are on screen. The first is the current one; "view older" adds the one before.
  const [shown, setShown] = useState(1);
  const [periods, setPeriods] = useState<Record<number, PeriodState>>({});

  // Load the newest period on screen: show what was loaded before at once, then refresh it in the background.
  // Rows already on screen stay.
  useEffect(() => {
    if (!props.email) return;
    const offset = -(shown - 1);
    const stale = peekTimeInOut(props.email, offset);
    if (stale) setPeriods((p) => ({ ...p, [offset]: stale }));
    // The request is shared with other callers of the same period (and with the warm-up in TimeInOut), so it is
    // not aborted; the answer is just dropped when this effect has been replaced or the page has gone.
    let ignore = false;
    getTimeInOutPeriod(props.email, offset)
      .then((response: any) => {
        if (!ignore) setPeriods((p) => ({ ...p, [offset]: response }));
      })
      .catch(() => {
        if (ignore) return;
        setPeriods((p) => (p[offset] ? p : { ...p, [offset]: 'error' }));
      });
    return () => {
      ignore = true;
    };
  }, [props.email, shown]);

  // When an older period is added, bring it into view inside the scrolling list so the user sees it loading.
  const newestRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (shown > 1) newestRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [shown]);

  const newest = periods[-(shown - 1)];
  const newestEmpty =
    newest === 'error' || (typeof newest === 'object' && !newest?.timeInOutDetailInfos?.length);
  // The button stays on screen, busy, while the period it asked for loads. It goes away once a period comes back
  // empty (nothing older) or the limit is reached.
  const loadingOlder = shown > 1 && newest === undefined;
  const canViewOlder =
    shown <= MAX_OLDER_PERIODS && (loadingOlder || (newest !== undefined && !newestEmpty));

  return (
    <div className="TimeInout_ThisWeek_detail text-center">
      <Row className="m-auto">
        <Col className="px-1">
          <div className="TimeInout_ThisWeek_detail_box">
            <div className="TimeInout_ThisWeek_detail_text">
              {t('time_list_box.timeanddate_in_out.title')}
            </div>
            <Row className="this-week-title-box m-1">
              <Col className="this-week-title px-1">
                {t('time_list_box.timeanddate_in_out.date')}
              </Col>
              <Col className="this-week-title px-1">
                {t('time_list_box.timeanddate_in_out.timein')}
              </Col>
              <Col className="this-week-title px-1">
                {t('time_list_box.timeanddate_in_out.timeout')}
              </Col>
              <Col className="this-week-title px-1">
                {t('time_list_box.timeanddate_in_out.time_absence')}
              </Col>
              <Col className="this-week-title px-1">
                {t('time_list_box.timeanddate_in_out.overtime')}
              </Col>
            </Row>
            <div className="w-100 table-container">
              {Array.from({ length: shown }, (_, i) => {
                const offset = -i;
                const period = periods[offset];
                const range = payPeriodRange(offset);
                return (
                  <div key={offset} ref={offset === -(shown - 1) ? newestRef : undefined}>
                    <div className="time-period-range">{range}</div>
                    {period === undefined ? (
                      <div className="time-skeleton" aria-busy="true">
                        {[0, 1, 2, 3, 4].map((n) => (
                          <div className="time-skeleton-row" key={n} />
                        ))}
                      </div>
                    ) : period === 'error' || !period.timeInOutDetailInfos?.length ? (
                      <div className="loader-data text-notfound">
                        {t('text_notfound')}
                      </div>
                    ) : (
                      <>
                        {period.timeInOutDetailInfos.map((item: any, index: number) => (
                          <Row
                            className={`time-in-out-list ${
                              item.qtyOT > 0 ? 'active1' : ' '
                            } ${item.qtyAbs > 0 ? 'active2' : ' '} m-1 mt-3`}
                            key={index}
                          >
                            <Col className="this-week-title-val px-1">
                              {Moment(item.dateWork).format('DD/M/YYYY')}
                            </Col>
                            <Col className="this-week-title-val px-1">
                              {item.timeIn ? item.timeIn : '-'}
                            </Col>
                            <Col className="this-week-title-val px-1">
                              {item.timeOut ? item.timeOut : '-'}
                            </Col>
                            <Col className="this-week-title-val px-1">
                              {item.qtyAbsStr ? item.qtyAbsStr : '-'}
                            </Col>
                            <Col className="this-week-title-val px-1">
                              {item.qtyOTStr ? item.qtyOTStr : '-'}
                            </Col>
                          </Row>
                        ))}
                        <Row className="time-in-out-list line-total m-1 mt-3">
                          <Col
                            xs={8}
                            className="this-week-title-val px-1  text-left"
                            style={{ textAlign: 'left' }}
                          >
                            <strong className="time-in-out-this-week-total text-left">
                              {t('time_list_box.timeanddate_in_out.total')}
                            </strong>
                          </Col>
                          <Col
                            xs={4}
                            className="this-week-title-val px-1  text-right pb-0"
                            style={{ textAlign: 'right' }}
                          >
                            <strong className="time-in-out-this-week-total">
                              {period.summary} {t('time_list_box.timeanddate_in_out.unit')}
                            </strong>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {canViewOlder && (
              <button
                type="button"
                className="time-view-older"
                disabled={loadingOlder}
                aria-busy={loadingOlder}
                onClick={() => setShown((n) => n + 1)}
              >
                {loadingOlder && <span className="spinner time-view-older-spinner" />}
                {loadingOlder
                  ? t('time_list_box.timeanddate_in_out.loading_older')
                  : t('time_list_box.timeanddate_in_out.view_older')}
              </button>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col className="m-2"></Col>
      </Row>
    </div>
  );
};
